// Chat Service - API functions for managing chat sessions and messages
// Backend has no Cognito; client sends user_id in body or query.
import apiClient from '../../../../../shared/lib/apiClient';
import { TokenManager } from '../../../../../shared/lib/apiClient';

export interface ChatSessionCreatePayload {
  user_id: string;
  project_id: string;
  name?: string;
}

export interface ChatSessionResponse {
  id: string;
  user_id: string;
  project_id: string;
  name?: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatMessageCreatePayload {
  user_id?: string; // Filled by chatApi from TokenManager; optional when calling createMessage
  session_id: string;
  project_id?: string;
  content: string;
  type: 'user' | 'assistant' | 'status';
  page?: 'requirements' | 'architecture' | 'code' | 'documents' | 'preview' | 'project_chat';
  channel?: string;
  is_contract?: boolean;
  contract_data?: any;
  agent?: string;
  llm?: string;
  progress?: number | null;
  conversational?: any;
  attachments?: any[];
  blocks?: any[];
}

export interface ChatMessageResponse {
  id: string;
  session_id: string;
  user_id: string;
  project_id?: string;
  content: string;
  type: string;
  timestamp: string;
  page?: string;
  channel?: string;
  is_contract?: boolean;
  contract_data?: any;
  agent?: string;
  llm?: string;
  progress?: number | null;
  conversational?: any;
  attachments?: any[];
  blocks?: any[];
  created_at: string;
}

function getUserId(): string {
  const id = TokenManager.getUserId();
  if (!id) throw new Error('User ID not found. Please sign in again.');
  return id;
}

class ChatService {
  /**
   * Create a new chat session for a project. Uses current user_id from TokenManager.
   */
  async createSession(payload: Omit<ChatSessionCreatePayload, 'user_id'>): Promise<ChatSessionResponse> {
    return apiClient<ChatSessionResponse>('/chat/sessions', {
      method: 'POST',
      body: { ...payload, user_id: getUserId() },
    });
  }

  /**
   * Get all chat sessions for the current user, optionally filtered by project
   */
  async getSessions(projectId?: string): Promise<ChatSessionResponse[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('user_id', getUserId());
    if (projectId) queryParams.append('project_id', projectId);
    return apiClient<ChatSessionResponse[]>(`/chat/sessions?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get a specific chat session
   */
  async getSession(sessionId: string): Promise<ChatSessionResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('user_id', getUserId());
    return apiClient<ChatSessionResponse>(`/chat/sessions/${sessionId}?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Create a chat message in a session. Uses current user_id from TokenManager.
   */
  async createMessage(payload: Omit<ChatMessageCreatePayload, 'user_id'>): Promise<ChatMessageResponse> {
    return apiClient<ChatMessageResponse>('/chat/messages', {
      method: 'POST',
      body: { ...payload, user_id: getUserId() },
    });
  }

  /**
   * Get all messages for a session, ordered by timestamp
   */
  async getSessionMessages(sessionId: string): Promise<ChatMessageResponse[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('user_id', getUserId());
    return apiClient<ChatMessageResponse[]>(`/chat/sessions/${sessionId}/messages?${queryParams.toString()}`, {
      method: 'GET',
    });
  }
}

export default new ChatService();
